import { useContext, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Button, Flex, Input } from 'antd';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';

import { useGetCreatorsCollections } from '@/api/monitor_collections';
import { useDebounce } from '@/hooks';
import { CollectionContext } from '@/providers/CollectionsContextProvider';
import { IPagination, TCollection } from '@/ts';

import AddCollectionModal from '../AddCollectionModal';
import EditCollectionModal from '../EditCollectionModal';
import CreatorsCollectionsTable from './CreatorsCollectionsTable';
import {
	creatorsCollectionsColumns,
	mockCreatorsCollectionsItems,
} from './data';

export default function CreatorsCollections() {
	const navigate = useNavigate();
	const { collection, setCollection, modalData, setModalData, setData } =
		useContext(CollectionContext);
	const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
	const [isEditCollectionModalOpen, setIsEditCollectionModalOpen] =
		useState(false);

	const [search, setSearch] = useState('');
	const [sort, setSort] = useState<'created_at'>('created_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

	const [pagination, setPagination] = useState<IPagination>({
		page: Number(localStorage.getItem('page') ?? 1),
		pageCount: 1,
		total: 0,
		limit: Number(localStorage.getItem('limit') ?? 10),
	});

	const debouncedSearch = useDebounce(search);

	const {
		data: creatorsData,
		isLoading,
		isFetching,
		isError,
		error,
	} = useGetCreatorsCollections({
		pagination,
		sortBy: sort,
		sortOrder,
		enabled: collection === 'creators',
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
	});

	const items = creatorsData?.data?.data?.items || [];
	const total = creatorsData?.data?.data?.pagination?.total || 0;

	const onTableChange = (__: any, _: any, sorter: any) => {
		setSort(sorter.field ?? 'requested_at');

		setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
	};

	const onEditCollectionClick = (data: any) => {
		setModalData(null);
		setIsEditCollectionModalOpen(true);
		setModalData(data);
	};

	const onViewClick = (collection: TCollection, data: any) => {
		setData(data);
		setCollection(collection);
		navigate(
			`/app/dashboard/video-insights-co-pilot/collections/creators/${data.id}`,
		);
	};

	if (collection !== 'creators') {
		return null;
	}

	return (
		<Flex vertical gap={16} align="flex-start" style={{ width: '100%' }}>
			<Flex vertical gap={16} align="flex-start" style={{ width: '100%' }}>
				<Flex
					align="center"
					justify="space-between"
					gap={16}
					style={{ width: '100%' }}
				>
					<Input
						placeholder="Search..."
						suffix={<SearchOutlined />}
						// style={{ width: "200px", maxWidth: "200px" }}
						onChange={(e) => setSearch(e.target.value)}
						value={search}
					/>
					<Button type="primary" onClick={() => setIsCollectionModalOpen(true)}>
						New creator collection
					</Button>
				</Flex>
				<CreatorsCollectionsTable
					data={items}
					columns={creatorsCollectionsColumns}
					onTableChange={onTableChange}
					isLoading={isLoading || isFetching}
					pagination={{ ...pagination, total }}
					setPagination={setPagination}
					onViewClick={onViewClick}
					onEditClick={onEditCollectionClick}
				/>
			</Flex>
			<AddCollectionModal
				isOpen={isCollectionModalOpen}
				setOpen={setIsCollectionModalOpen}
				type="creators"
			/>
			<EditCollectionModal
				isOpen={isEditCollectionModalOpen}
				setOpen={setIsEditCollectionModalOpen}
				data={{
					...modalData,
					type: 'creators',
				}}
				setData={setModalData}
			/>
		</Flex>
	);
}
